import React from 'react';

interface TypographyProps {
  variant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'body1'
    | 'body2'
    | 'caption'
    | 'subtitle1'
    | 'subtitle2';
  color?: 'primary' | 'secondary' | 'tertiary' | 'accent';
  align?: 'left' | 'center' | 'right' | 'justify';
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  component?: React.ElementType;
}

const Typography: React.FC<TypographyProps> = ({
  variant = 'body1',
  color = 'primary',
  align = 'left',
  children,
  className = '',
  style = {},
  component,
}) => {
  const getVariantStyles = (): { tag: React.ElementType; classes: string } => {
    switch (variant) {
      case 'h1':
        return {
          tag: 'h1',
          classes: 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold',
        };
      case 'h2':
        return {
          tag: 'h2',
          classes: 'text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold',
        };
      case 'h3':
        return {
          tag: 'h3',
          classes: 'text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold',
        };
      case 'h4':
        return {
          tag: 'h4',
          classes: 'text-base sm:text-lg md:text-xl lg:text-2xl font-semibold',
        };
      case 'h5':
        return {
          tag: 'h5',
          classes: 'text-sm sm:text-base md:text-lg lg:text-xl font-medium',
        };
      case 'h6':
        return {
          tag: 'h6',
          classes: 'text-xs sm:text-sm md:text-base lg:text-lg font-medium',
        };
      case 'subtitle1':
        return { tag: 'p', classes: 'text-base sm:text-lg font-medium' };
      case 'subtitle2':
        return { tag: 'p', classes: 'text-sm sm:text-base font-medium' };
      case 'body1':
        return { tag: 'p', classes: 'text-sm sm:text-base' };
      case 'body2':
        return { tag: 'p', classes: 'text-xs sm:text-sm' };
      case 'caption':
        return { tag: 'span', classes: 'text-xs' };
      default:
        return { tag: 'p', classes: 'text-sm sm:text-base' };
    }
  };

  const getColorStyles = (): React.CSSProperties => {
    switch (color) {
      case 'secondary':
        return { color: 'var(--text-secondary)' };
      case 'tertiary':
        return { color: 'var(--text-tertiary)' };
      case 'accent':
        return { color: 'var(--accent)' };
      default:
        return { color: 'var(--text-primary)' };
    }
  };

  const getAlignClass = (): string => {
    switch (align) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      case 'justify':
        return 'text-justify';
      default:
        return 'text-left';
    }
  };

  const { tag: defaultTag, classes: variantClasses } = getVariantStyles();
  const Component = component || defaultTag;

  const combinedClasses = [variantClasses, getAlignClass(), className]
    .filter(Boolean)
    .join(' ');

  const combinedStyles: React.CSSProperties = {
    ...getColorStyles(),
    ...style,
  };

  return (
    <Component className={combinedClasses} style={combinedStyles}>
      {children}
    </Component>
  );
};

export default Typography;
